import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';
import { CommonService } from '../services/Common-function/common.service';

/**
 * Pins the filter row under the fixed header while the page scrolls: adds `fixed-top-section` and sets
 * CommonService.isSetFilterPositionFixed (the navbar then moves its floating pill below the row).
 * The row is pinned at the header's bottom edge, so the top strips (Diwali / campaign) and the live runner are
 * taken into account, and a placeholder keeps its space so the list does not jump.
 * Usage: <div [appNavbarChangeClass]="true"> (false: the row scrolls with the page)
 */
@Directive({
  selector: '[appNavbarChangeClass]'
})
export class NavbarChangeClassDirective implements OnDestroy {
  /** Pin the row while the page scrolls */
  @Input('appNavbarChangeClass') isEnabled: boolean | '' = true;

  /** Takes the row's place in the page while it is pinned */
  private placeholder: HTMLElement | null = null;

  constructor(private relRef: ElementRef<HTMLElement>, private commonService: CommonService, private renderer: Renderer2) { }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isEnabled === false) {
      this.unpin();
      return;
    }
    const header = document.getElementById('navbar');
    const headerBottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    // where the row belongs in the page: its placeholder while pinned, the row itself otherwise
    const rowTop = (this.placeholder || this.relRef.nativeElement).getBoundingClientRect().top;
    if (rowTop <= headerBottom) {
      this.pin(headerBottom);
    } else {
      this.unpin();
    }
  }

  ngOnDestroy(): void {
    this.unpin();
  }

  private pin(headerBottom: number): void {
    const element = this.relRef.nativeElement;
    if (!this.placeholder) {
      this.placeholder = this.renderer.createElement('div');
      this.renderer.setStyle(this.placeholder, 'height', `${element.offsetHeight}px`);
      this.renderer.insertBefore(element.parentNode, this.placeholder, element);
      this.renderer.addClass(element, 'fixed-top-section');
      this.commonService.isSetFilterPositionFixed = true;
    }
    this.renderer.setStyle(element, 'top', `${headerBottom}px`);
  }

  private unpin(): void {
    if (!this.placeholder) {
      return;
    }
    const element = this.relRef.nativeElement;
    this.renderer.removeChild(element.parentNode, this.placeholder);
    this.placeholder = null;
    this.renderer.removeClass(element, 'fixed-top-section');
    this.renderer.removeStyle(element, 'top');
    this.commonService.isSetFilterPositionFixed = false;
  }
}
